/**
 * Flatpickr 래퍼 – 날짜/시간 입력에 Flatpickr 적용.
 * 컨트롤러에서는 new DatePicker(selector, options).init() 만 사용.
 */
declare global {
  interface Window {
    flatpickr?: FlatpickrFn & { l10ns?: Record<string, unknown> };
  }
}

interface FlatpickrFn {
  (el: HTMLElement, options?: FlatpickrOptions): FlatpickrInstance;
}

interface FlatpickrOptions {
  enableTime?: boolean;
  noCalendar?: boolean;
  dateFormat?: string;
  time_24hr?: boolean;
  locale?: string;
  allowInput?: boolean;
  [key: string]: unknown;
}

interface FlatpickrInstance {
  destroy: () => void;
}

export interface DatePickerOptions {
  enableTime?: boolean;
  noCalendar?: boolean;
  dateFormat?: string;
  time24hr?: boolean;
  locale?: string;
  allowInput?: boolean;
}

export class DatePicker {
  private readonly target: string | HTMLElement;
  private readonly options: DatePickerOptions;
  private instance: FlatpickrInstance | null = null;

  constructor(target: string | HTMLElement, options: DatePickerOptions = {}) {
    this.target = target;
    this.options = options;
  }

  init(): FlatpickrInstance | null {
    const fp = typeof window !== "undefined" ? window.flatpickr : undefined;
    if (typeof fp !== "function") return null;

    const el =
      typeof this.target === "string"
        ? document.querySelector<HTMLElement>(this.target)
        : this.target;
    if (!el) return null;

    const locale =
      this.options.locale ??
      (window.flatpickr?.l10ns?.ko ? "ko" : undefined);

    this.instance = fp(el, {
      enableTime: this.options.enableTime,
      noCalendar: this.options.noCalendar,
      dateFormat: this.options.dateFormat ?? "Y-m-d",
      time_24hr: this.options.time24hr ?? true,
      locale,
      allowInput: this.options.allowInput ?? true,
    });
    return this.instance;
  }

  getInstance(): FlatpickrInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.instance?.destroy) {
      this.instance.destroy();
      this.instance = null;
    }
  }
}
